import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingUp,
  Target,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  Eye,
  MousePointerClick,
  Search,
  BarChart3,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function SharedReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API_URL}/reports/shared/${token}`);
        setReport(res.data);
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) setError('This report link is invalid or has been removed.');
        else if (status === 410) setError('This share link has expired.');
        else setError('Failed to load report.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Report Unavailable</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const stats = report?.executive_summary || {};
  const hasGA4 = report?.data_sources?.ga4;
  const hasGSC = report?.data_sources?.gsc;

  const formatChange = (value) => {
    if (!value && value !== 0) return null;
    const isPositive = value > 0;
    return (
      <span className={`inline-flex items-center gap-1 text-sm ${isPositive ? 'text-emerald-300' : 'text-red-300'}`}>
        {isPositive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
        {isPositive ? '+' : ''}{value}%
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <p className="text-xs uppercase tracking-wider text-indigo-500 font-semibold mb-2">Shared Report</p>
          <h1 className="text-2xl font-bold">{report?.period || 'Monthly'} Performance Report</h1>
          {hasGA4 || hasGSC ? (
            <p className="text-sm text-slate-500 mt-1">
              Data from {hasGA4 && 'Google Analytics'}{hasGA4 && hasGSC && ' & '}{hasGSC && 'Search Console'}
            </p>
          ) : null}
        </div>

        {/* Executive Summary */}
        <div className="rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white p-8">
          <h2 className="font-bold text-lg mb-4">Executive Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-white/10">
              <p className="text-indigo-100 text-sm mb-1">Organic Traffic</p>
              <p className="text-2xl font-bold">{stats.organic_traffic?.toLocaleString() || 0}</p>
              {formatChange(stats.traffic_change)}
            </div>
            <div className="p-4 rounded-xl bg-white/10">
              <p className="text-indigo-100 text-sm mb-1">Conversions</p>
              <p className="text-2xl font-bold">{stats.conversions?.toLocaleString() || 0}</p>
              {formatChange(stats.conversion_change)}
            </div>
            <div className="p-4 rounded-xl bg-white/10">
              <p className="text-indigo-100 text-sm mb-1">Search Clicks</p>
              <p className="text-2xl font-bold">{stats.total_clicks?.toLocaleString() || 0}</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10">
              <p className="text-indigo-100 text-sm mb-1">Keywords</p>
              <p className="text-2xl font-bold">{stats.keyword_count?.toLocaleString() || 0}</p>
            </div>
          </div>
        </div>

        {/* Traffic Chart */}
        {report?.traffic_data?.chart_data?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-indigo-500" />
              Traffic Growth
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={report.traffic_data.chart_data}>
                  <defs>
                    <linearGradient id="tg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                  <Area type="monotone" dataKey="traffic" stroke="#6366f1" fillOpacity={1} fill="url(#tg)" strokeWidth={2} name="Sessions" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Top Content */}
        {report?.content_data?.top_content?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-violet-500" />
              Top Content
            </h3>
            <div className="space-y-2">
              {report.content_data.top_content.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="font-medium text-sm truncate">{c.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.views?.toLocaleString()} views</p>
                  </div>
                  {c.conversions > 0 && (
                    <span className="text-xs font-medium bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                      {c.conversions} conversions
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-slate-400">
          Generated by GDS &middot; {new Date().toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
